<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <div class="d-flex flex-row h-100 w-100">
      <div
        class="slim-sidebar d-flex flex-column align-items-center py-1 border-right"
      >
        <div
          id="tour-switch-tabs"
          v-b-tooltip="{ title: $t('navMenu.messages'), placement: 'right', trigger: 'hover' }"
          class="sidebar-icon rounded-circle mb-1 cursor-pointer d-flex justify-content-center align-items-center"
          :class="{ 'active-icon': activeMode === 'messages' }"
          @click="activeMode = 'messages'"
        >
          <feather-icon icon="MessageSquareIcon" size="22" />
        </div>

        <div
          id="tour-switch-tabs-contact"
          v-b-tooltip="{ title: $t('navMenu.contacts'), placement: 'right', trigger: 'hover' }"
          class="sidebar-icon rounded-circle mb-1 cursor-pointer d-flex justify-content-center align-items-center"
          :class="{ 'active-icon': activeMode === 'contacts' }"
          @click="activeMode = 'contacts'"
        >
          <feather-icon icon="UsersIcon" size="22" />
        </div>

        <div
          v-b-tooltip="{ title: $t('language.title'), placement: 'right', trigger: 'hover' }"
          class="sidebar-icon rounded-circle mb-1 cursor-pointer d-flex justify-content-center align-items-center"
          :class="{ 'active-icon': activeMode === 'language' }"
          @click="activeMode = 'language'"
        >
          <feather-icon icon="GlobeIcon" size="22" />
        </div>

        <div
          v-b-tooltip="{ title: $t('navMenu.eula'), placement: 'right', trigger: 'hover' }"
          class="sidebar-icon rounded-circle mb-1 cursor-pointer d-flex justify-content-center align-items-center"
          @click="openTermsModal"
        >
          <feather-icon icon="FileTextIcon" size="22" />
        </div>

        <div
          v-b-tooltip="{ title: $t('navMenu.startTour') || 'Start Tour', placement: 'right', trigger: 'hover' }"
          class="sidebar-icon rounded-circle mb-1 cursor-pointer d-flex justify-content-center align-items-center"
          @click="startOnboardingTour"
        >
          <feather-icon icon="HelpCircleIcon" size="22" />
        </div>

        <ul class="mt-auto d-flex justify-content-center align-items-center list-unstyled mb-0 p-0">
          <dark-toggler
            v-b-tooltip="{ title: $t('navMenu.themeToggle'), placement: 'right', trigger: 'hover' }"
          />
        </ul>
      </div>

      <div class="main-nav-area flex-grow-1 d-flex flex-column overflow-hidden position-relative border-right border-left-1 border-1">

        <div
          :class="{'d-block': shallShadowBottom}"
          class="shadow-bottom"
        />

        <vue-perfect-scrollbar
          :settings="perfectScrollbarSettings"
          class="main-menu-content scroll-area flex-grow-1"
          tagname="div"
          @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
        >
          <div v-if="activeMode === 'messages'" class="component-wrapper p-0 h-100 d-flex flex-column">
            <post-search />
          </div>

          <div v-if="activeMode === 'contacts'" class="component-wrapper p-0 h-100 d-flex flex-column">
            <contacts-page />
          </div>

          <div v-if="activeMode === 'language'" class="component-wrapper p-0 h-100 d-flex flex-column">
            <language-selector />
          </div>
        </vue-perfect-scrollbar>

      </div>
    </div>
    <eula ref="eulaComponent" />
  </div>
</template>

<script>
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import {
  computed, getCurrentInstance, onMounted, onUnmounted, ref,
} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import PostSearch from '@/views/PostSearch/PostSearch.vue'
import ContactsPage from '@/views/contacts/Contacts.vue'
import LanguageSelector from '@/views/LanguageSelector.vue'
import DarkToggler from '@core/layouts/components/app-navbar/components/DarkToggler.vue'
import Eula from '@/views/eula/Eula.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    PostSearch,
    ContactsPage,
    LanguageSelector,
    DarkToggler,
    Eula,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      activeMode: 'messages',
    }
  },
  methods: {
    startOnboardingTour() {
      this.activeMode = 'messages';
      this.$store.commit('app/SET_IS_ONBOARDING', true);
    },
    openTermsModal() {
      if (this.$refs.eulaComponent && this.$refs.eulaComponent.showModal) {
        this.$refs.eulaComponent.showModal();
      }
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    const vm = getCurrentInstance().proxy;
    const handleMobileMenuToggle = forceOpen => {
      if (forceOpen !== props.isVerticalMenuActive) {
        props.toggleVerticalMenuActive();
      }
    };

    const handleModeChange = newMode => {
      vm.activeMode = newMode;
    };

    const handleShowTermsModal = () => {
      vm.openTermsModal();
    };

    onMounted(() => {
      vm.$root.$on('toggle-mobile-menu', handleMobileMenuToggle);
      vm.$root.$on('change-active-mode', handleModeChange);
      vm.$root.$on('show-terms-modal', handleShowTermsModal);
    });

    onUnmounted(() => {
      vm.$root.$off('toggle-mobile-menu', handleMobileMenuToggle);
      vm.$root.$off('change-active-mode', handleModeChange);
      vm.$root.$off('show-terms-modal', handleShowTermsModal);
    });

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
      suppressScrollX: true,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    const shallShadowBottom = ref(false)

    return {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
      collapseTogglerIconFeather,
      shallShadowBottom,
      perfectScrollbarSettings,
      skin,
    }
  },
}
</script>

<style lang="scss">
.light-layout .main-menu {
  background-color: transparent !important;
}
/* Background area for the slim sidebar */
.light-layout .slim-sidebar {
  background-color: #E4E1ED !important;
}

/* Background area for the main section */
.light-layout .main-nav-area {
  background-color: #F0EEF5 !important;
}

/* --- Dark Mode Layout --- */
.dark-layout .main-menu {
  background-color: transparent !important;
}

/* "Navigation" color for the slim sidebar */
.dark-layout .slim-sidebar {
  background-color: #2A2A32 !important;
}
/* Background area for the main section */
.dark-layout .main-nav-area {
  background-color: #202029 !important;
}
</style>

<style lang="scss" scoped>
.slim-sidebar {
  width: 70px;
  min-width: 70px;
  z-index: 2;
}

.sidebar-icon {
  width: 45px;
  height: 45px;
  border: 2px solid transparent;
  transition: all 0.2s ease-in-out;
  color: #6e6b7b;

  &:hover {
    background-color: rgba(130, 134, 139, 0.12);
  }

  &.active-icon {
    border-color: var(--primary, #7367f0);
    background-color: rgba(115, 103, 240, 0.12);
    color: var(--primary, #7367f0) !important;
  }
}

.menu-dark .sidebar-icon {
  color: #d0d2d6;
  &.active-icon {
    background-color: rgba(115, 103, 240, 0.25);
  }
}
</style>
